import React, { forwardRef, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import useTranslate from '../../../../../../common/hooks/useTranslate';
import { getFieldName } from '../../../../../../common/utility/getFieldName';
import replaceDotsWithUnderscores from '../../../../../../common/utility/replaceDotsWithUnderscores';
import _ from 'lodash';

import { aggregateBy, AggregateDescriptor, AggregateResult, orderBy, process, State } from '@progress/kendo-data-query';
import { setExpandedState, setGroupIds } from '@progress/kendo-react-data-tools';
import { useInternationalization } from '@progress/kendo-react-intl';
import { Grid, GridColumn, GridCustomCellProps, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';

import FilterCtx from '../../../../../../context/filterContext/filterCtx';

import { SinglePeriodGridProps } from './singlePeriodGridProps';
import { IGridPreparedData } from '../types/IGridPreparedData';
import { ICurrency } from '../../../../types/ICurrency';
import { IBoardMeasure } from '../../../../types/IBoardMeasure';
import { IGridDefinitionColumn } from '../types/IGridDefinitionColumn';

import LinkCell from '../../../../../ui/table/cells/linkCell/linkCell';
import ColumnMenu from '../../../columnMenu/columnMenu';
import TextCell from '../../../../../ui/table/cells/textCell/textCell';
import CashflowCell from '../../cashflow/cells/cashflowCell/cashflowCell';
import GridFooterCell from '../cells/gridFooterCell/gridFooterCell';
import StaticPropertyCell from '../cells/staticPropertyCell/staticPropertyCell';
import MobileDataItem from '../cells/mobileDataItem/mobileDataItem';
import DateCell from '../../../../../ui/table/cells/dateCell/dateCell';
import NumericCell from '../../../cells/numericCell/numericCell';

const SinglePeriodGrid: React.FC<SinglePeriodGridProps> = forwardRef(
  ({
     gridData,
     filter,
     sort,
     grouping,
     widget,
     gridColumns,
     selectedCurrency,
     openCashflowDialog,
     gridClassName,
     boardData,
     boards,
     isMobile,
     setup,
     sortDescriptors,
   }, ref: any) => {
    const filterCtx = useContext(FilterCtx);
    const params = useParams();
    const intl = useInternationalization();
    const { t } = useTranslate();

    const workingGridData = _.cloneDeep(gridData);

    const initialDataState = {
      //...filterCtx.gridWidgetFilterState,
      take: undefined,
      sort: sort,
      filter: filter,
      group: grouping,
      skip: 0,
    };

    const NAME_COL_WIDTH = 300;
    const CURRENCY_COL_WIDTH = 120;

    const hasAggregates = grouping.some((g) => g.showAggregates);

    const [dataState, setDataState] = useState<any>(filterCtx.gridWidgetFilterState);
    const [resultState, setResultState] = useState<any>({});
    const [multipleCurrencies, setMultipleCurrencies] = useState(false);
    const [collapsedState, setCollapsedState] = React.useState<string[]>([]);
    const [aggregateResult, setAggregateResult] = useState<AggregateResult>();

    const getVisibleColumns = (gridColumns, grouping) => {
      return gridColumns.filter((col) => {
        const groupItem = grouping.find((g) => g.field === getFieldName(col, false));
        return !(groupItem?.hideColumn === true || col.hidden === true);
      });
    };

    const visibleColumns = useMemo(() => getVisibleColumns(gridColumns, grouping), [gridColumns, grouping]);
    const visibleMeasures = visibleColumns.map(col => boardData?.measures?.find(m => m.id === col.measureId));

    const checkIFThereIsTotal = (columns) => {
      let total = false;
      columns.forEach((column) => {
        if (column.total) {
          total = true;
        }
      });
      return total;
    };

    const LinkCellRender = (props) => {
      return widget.linkContentId && boards.find((item) => item.id === widget.linkContentId) ? (
        <LinkCell {...props} to={`/boards/${params.investorId}/${widget.linkContentId}/${props.dataItem.objectId}`} />
      ) : (
        <TextCell {...props} />
      );
    };


    const StaticPropertyRender = () => {
      return (props) => <StaticPropertyCell {...props} />;
    };

    const CashflowCellRender = (column) => {
      return (props) => <CashflowCell {...props} columnName={column.name} selectedCurrency={selectedCurrency}
                                      boardId={params.boardId || boardData.boardId}
                                      handleCashflowOpen={openCashflowDialog} singlePeriod={setup?.singlePeriod}
                                      scale={column.scale}
                                      investorObjectId={params.investorId || widget.investorObjectId} />;
    };

    const FooterCellRender = (gridData: IGridPreparedData[], totalCount: boolean, columnScale: number, measure: IBoardMeasure) => {
      return (props) => (
        <GridFooterCell {...props} gridData={gridData} includeTotalCount={totalCount}
                        scale={columnScale || measure?.scale} isPercent={measure?.isPercent} />
      );
    };

    const MeasureCellRender = (columnScale: number, measure: IBoardMeasure) => {
      return (props) =>
        measure?.dataType === 'date' ? (
          <DateCell {...props} className='align-right' />
        ) : (
          <NumericCell {...props} scale={columnScale || measure?.scale} isPercent={measure?.isPercent} />
        );
    };

    const groupFooterRender = (column: IGridDefinitionColumn) => {

      const measure = boardData?.measures?.find((m) => m.id === column.measureId);

      return (props: GridCustomCellProps) => {
        let amount = null;
        const aggregates = props.dataItem.aggregates;
        if (props.field && aggregates && aggregates[props.field]) {
          amount = aggregates[props.field].sum || 0;
        }
        if (amount === null) {
          return <td style={{ padding: 0 }}>
            <div></div>
          </td>;
        }

        const formatAmount = intl.toString(amount, `${measure?.isPercent ? 'p' : 'n'}${measure?.scale || 0}`);
        return <td className={'numeric-cell alignment-right'}>{formatAmount}</td>;
      };
    };

    const sortGroupsRecursively = (groups, grouping, level = 0) => {
      if (!Array.isArray(groups) || groups.length === 0) return groups;

      const dir = grouping[level]?.dir || 'asc';
      const sorted = orderBy(groups, [{ field: 'value', dir }]);

      return sorted.map((group) => ({
        ...group,
        items:
          Array.isArray(group.items) && group.items.length > 0 && group.items[0].hasOwnProperty('value')
            ? sortGroupsRecursively(group.items, grouping, level + 1)
            : group.items,
      }));
    };

    const getAggregateDescriptors = (columns: IGridDefinitionColumn[]) => {
      const aggregates: AggregateDescriptor[] = [];
      columns.forEach((col) => {
        if (col.total) aggregates.push({ field: getFieldName(col, false) || '', aggregate: 'sum' });
      });
      return aggregates;
    };


    const processWithGroups = (data: any[], state: State) => {
      const processed = process(data, state);
      setGroupIds({ data: processed.data, group: state.group });

      processed.data = sortGroupsRecursively(processed.data, state.group);

      return processed;
    };


    const hasDifferentCurrencies = (data: any[], focusedCurrency?: ICurrency) => {
      if (!focusedCurrency || data.length == 0) return false;
      if (focusedCurrency.currency !== '_') return false;

      const currency = data[0].currency;
      return !!data.find((item) => item.currency !== currency);
    };

    const onExpandChange = React.useCallback(
      (event) => {
        const item = event.dataItem;

        if (item.groupId) {
          const newCollapsedIds = !event.value ? [...collapsedState, item.groupId] : collapsedState.filter((groupId) => groupId !== item.groupId);
          setCollapsedState(newCollapsedIds);
        }
      },
      [collapsedState],
    );


    const handleDataStateChange = useCallback(
      (e: GridDataStateChangeEvent) => {
        const updatedState = processWithGroups(workingGridData, e.dataState);
        setDataState(e.dataState);
        filterCtx.setGridWidgetFilterState(e.dataState);
        setResultState(updatedState);
        setAggregateResult(aggregateBy(workingGridData, getAggregateDescriptors(gridColumns)));
      },
      [gridData],
    );


    const newData = useMemo(() => {
      if (grouping.length > 0) {
        return setExpandedState({
          data: resultState.data ? resultState.data : [],
          collapsedIds: collapsedState,
        });
      }

      return resultState.data || [];
    }, [resultState, grouping, collapsedState]);


    useEffect(() => {
      //@ts-ignore
      setDataState(initialDataState);
      filterCtx.setGridWidgetFilterState(initialDataState);
      setResultState(processWithGroups(workingGridData, initialDataState));
      setMultipleCurrencies(hasDifferentCurrencies(workingGridData, selectedCurrency));
      setAggregateResult(aggregateBy(workingGridData, getAggregateDescriptors(gridColumns)));
    }, [gridData]);


    return (
      <>
        {isMobile && <ul className='mobile-grid-wrapper'>
          {resultState.data?.map((dataItem, key) => {
            return <MobileDataItem dataItem={dataItem} key={key} boardId={params.boardId || boardData.boardId}
                                   selectedCurrency={selectedCurrency}
                                   investorObjectId={params.investorId || widget.investorObjectId}
                                   staticProperties={boardData.staticProperties} columns={gridColumns}
                                   measures={visibleMeasures}
                                   handleOpenCashflow={openCashflowDialog} singlePeriod={setup.singlePeriod}
                                   link={`/boards/${params.investorId}/${widget.linkContentId}/${dataItem.objectId}`}
                                   isLink={widget.linkContentId && boards.find((item) => item.id === widget.linkContentId)} />;
          })}
        </ul>}
        {!isMobile && <Grid
          data={newData}
          {...dataState}
          onDataStateChange={handleDataStateChange}
          resizable={true}
          sortable={{ mode: 'multiple' }}
          groupable={{
            //enabled: true,
            footer: hasAggregates ? 'visible' : 'none',
          }}
          onExpandChange={onExpandChange}
          expandField='expanded'
          className={`numeric-table-style-wrapper ${gridClassName ? gridClassName : ''}`}
          scrollable='scrollable'
          lockGroups={true}>

          <GridColumn
            field='name'
            title={t('NAME')}
            width={NAME_COL_WIDTH}
            filter='text'
            columnMenu={ColumnMenu}
            locked={true}
            cells={{
              data: LinkCellRender,
              footerCell: checkIFThereIsTotal(gridColumns) ? (props) => (
                <td {...props} style={props.style}
                    className='total-footer-cell'>{t('WIDGET.TOTAL')}</td>) : undefined,
            }} />
          {selectedCurrency?.currency === '_' &&
          <GridColumn field='currency' title={t('WIDGET.GRID_CURRENCY')} width={120} />}
          {visibleColumns.map((column, index) => {
            const measure = boardData.measures?.find((m) => m.id === column.measureId);
            if (column.type === 'property') {
              return (
                <GridColumn
                  field={getFieldName(column, false)}
                  title={column.name}
                  width={227}
                  key={index}
                  sortable={true}
                  filter='text'
                  headerClassName={'table-header-center-wrap'}
                  cell={StaticPropertyRender()}
                />
              );
            }
            return (
              <GridColumn
                field={getFieldName(column, false)}
                title={column.name}
                width={205}
                key={index}
                sortable={true}
                groupable={true}
                cells={{
                  data: column.viewCashflows ? CashflowCellRender(column) : MeasureCellRender(column?.scale, measure),
                  groupFooter: column.total ? groupFooterRender(column) : undefined,
                  footerCell: column.total ? FooterCellRender(gridData, column.total && !multipleCurrencies, column?.scale, measure) : undefined,
                }}
                headerClassName={'table-header-center-wrap'}
              />
            );
          })}
        </Grid>}


        <ExcelExport data={resultState.data} ref={ref} fileName={replaceDotsWithUnderscores(widget.name) || t('EXPORT')} group={dataState.group}>
          <ExcelExportColumn field='name' title={t('NAME')} width={NAME_COL_WIDTH} locked={true} />

          {selectedCurrency?.currency === '_' &&
          <ExcelExportColumn field='currency' title={t('CURRENCY')} width={CURRENCY_COL_WIDTH} />}

          {visibleColumns.map((column: IGridDefinitionColumn) => {
            const fieldName = getFieldName(column, false);

            if (column.type === 'property' || !column.total) return <ExcelExportColumn field={fieldName}
                                                                                       title={column.name} />;

            return (
              <ExcelExportColumn
                field={fieldName}
                title={column.name}
                footer={(props) => {
                  if (aggregateResult && aggregateResult[props.column.field]) return aggregateResult[props.column.field].sum || 0;
                }}
                groupFooterCellOptions={{ format: '' }}
                groupFooter={(props) => {
                  if (props.aggregates && props.aggregates[props.field]) return props.aggregates[props.field].sum || 0;
                }}
              />
            );
          })}
        </ExcelExport>

      </>
    );
  },
);

export default SinglePeriodGrid;
